import React, { useState } from "react";
import {
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    IconButton,
    Breadcrumbs,
    Link,
    Paper,
    Tooltip,
    InputBase,
    Divider,
} from "@mui/material";
import FolderIcon from "@mui/icons-material/Folder"; // File explorer folder icon
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import DescriptionIcon from "@mui/icons-material/Description";
import SearchIcon from "@mui/icons-material/Search";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import DownloadIcon from "@mui/icons-material/Download";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import meetingData from "./meetingData.json";

interface Meeting {
    id: string;
    title: string;
    date: string;
    video: {
        name: string;
        url: string;
    };
    notes: {
        name: string;
        url: string;
    };
}

interface MonthGroup {
    monthYear: string;
    meetings: Meeting[];
}

const MeetingRecordsPage = () => {
    const [view, setView] = useState<"months" | "meetings" | "files">("months");
    const [selectedMonth, setSelectedMonth] = useState<MonthGroup | null>(null);
    const [selectedMeeting, setSelectedMeeting] = useState<Meeting | null>(null);
    const [searchTerm, setSearchTerm] = useState("");

    const handleMonthClick = (month: MonthGroup) => {
        setSelectedMonth(month);
        setView("meetings");
    };

    const handleMeetingClick = (meeting: Meeting) => {
        setSelectedMeeting(meeting);
        setView("files");
    };

    const handleBack = () => {
        if (view === "files") {
            setView("meetings");
            setSelectedMeeting(null);
        } else if (view === "meetings") {
            setView("months");
            setSelectedMonth(null);
        }
    };

    const handleBreadcrumbClick = (targetView: "months" | "meetings") => {
        if (targetView === "months") {
            setView("months");
            setSelectedMonth(null);
            setSelectedMeeting(null);
        } else if (targetView === "meetings") {
            setView("meetings");
            setSelectedMeeting(null);
        }
    };

    const filteredData = meetingData.filter((monthGroup) => {
        if (view === "months") {
            return monthGroup.monthYear.toLowerCase().includes(searchTerm.toLowerCase());
        } else if (view === "meetings" && selectedMonth) {
            return selectedMonth.meetings.some((m) =>
                m.title.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }
        return true;
    });

    const renderMonths = () => (
        <Grid container spacing={3}>
            {filteredData.map((month) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={month.monthYear}>
                    <Card
                        sx={{
                            cursor: "pointer",
                            transition: "all 0.3s ease",
                            "&:hover": {
                                transform: "translateY(-5px)",
                                boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                                "& .folder-icon": { color: "#ffca28" },
                            },
                            borderRadius: "12px",
                            border: "1px solid #edf2f7",
                        }}
                        onClick={() => handleMonthClick(month)}
                    >
                        <CardContent
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                pt: 4,
                            }}
                        >
                            <FolderIcon
                                className="folder-icon"
                                sx={{ fontSize: 80, color: "#ffb300", mb: 2, transition: "color 0.3s" }}
                            />
                            <Typography variant="h6" sx={{ fontWeight: 600, color: "#2d3748" }}>
                                {month.monthYear}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                {month.meetings.length} Meetings
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            ))}
        </Grid>
    );

    const renderMeetings = () => {
        if (!selectedMonth) return null;
        const meetings = selectedMonth.meetings.filter((m) =>
            m.title.toLowerCase().includes(searchTerm.toLowerCase())
        );

        return (
            <Grid container spacing={3}>
                {meetings.map((meeting) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={meeting.id}>
                        <Card
                            sx={{
                                cursor: "pointer",
                                transition: "all 0.3s ease",
                                "&:hover": {
                                    transform: "translateY(-5px)",
                                    boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                                    "& .folder-icon": { color: "#ffca28" },
                                },
                                borderRadius: "12px",
                                border: "1px solid #edf2f7",
                            }}
                            onClick={() => handleMeetingClick(meeting)}
                        >
                            <CardContent
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    pt: 4,
                                }}
                            >
                                <FolderIcon
                                    className="folder-icon"
                                    sx={{ fontSize: 80, color: "#ffc107", mb: 2 }}
                                />
                                <Typography
                                    variant="subtitle1"
                                    sx={{ fontWeight: 600, color: "#2d3748", textAlign: "center" }}
                                >
                                    {meeting.title}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                    {meeting.date}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>
        );
    };

    const renderFiles = () => {
        if (!selectedMeeting) return null;

        const files = [
            {
                type: "video",
                name: "Recorded Video",
                fileName: selectedMeeting.video.name,
                icon: <VideoLibraryIcon sx={{ fontSize: 48, color: "#e53e3e" }} />,
                url: selectedMeeting.video.url,
            },
            {
                type: "notes",
                name: "Google Meeting Final Notes",
                fileName: selectedMeeting.notes.name,
                icon: <DescriptionIcon sx={{ fontSize: 48, color: "#3182ce" }} />,
                url: selectedMeeting.notes.url,
            },
        ];

        return (
            <Grid container spacing={4}>
                {files.map((file, idx) => (
                    <Grid item xs={12} sm={6} key={idx}>
                        <Paper
                            elevation={0}
                            sx={{
                                p: 3,
                                borderRadius: "16px",
                                border: "1px solid #e2e8f0",
                                display: "flex",
                                alignItems: "center",
                                gap: 3,
                                transition: "background 0.2s",
                                "&:hover": {
                                    bgcolor: "#f7fafc",
                                },
                            }}
                        >
                            <Box sx={{ p: 2, bgcolor: "white", borderRadius: "12px", boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }}>
                                {file.icon}
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5, color: "#1a202c" }}>
                                    {file.name}
                                </Typography>
                                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                                    {file.fileName}
                                </Typography>
                                <Box sx={{ display: "flex", gap: 1 }}>
                                    <Tooltip title="View Recording">
                                        <IconButton
                                            size="small"
                                            sx={{ bgcolor: "#edf2f7", "&:hover": { bgcolor: "#e2e8f0" } }}
                                            onClick={() => window.open(file.url, "_blank")}
                                        >
                                            <OpenInNewIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Download">
                                        <IconButton
                                            size="small"
                                            sx={{ bgcolor: "#edf2f7", "&:hover": { bgcolor: "#e2e8f0" } }}
                                        >
                                            <DownloadIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            </Box>
                        </Paper>
                    </Grid>
                ))}
            </Grid>
        );
    };

    return (
        <Box sx={{ minHeight: "100vh", bgcolor: "#f8fafc" }}>
            {/* Header Section */}
            <Box sx={{ mb: 4, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 800, color: "#1a202c", mb: 1 }}>
                        Meeting Records
                    </Typography>
                    <Breadcrumbs separator={<KeyboardArrowRightIcon fontSize="small" />} sx={{ mb: 2 }}>
                        <Link
                            underline="hover"
                            color="inherit"
                            sx={{ cursor: "pointer", display: "flex", alignItems: "center" }}
                            onClick={() => handleBreadcrumbClick("months")}
                        >
                            Home
                        </Link>
                        {selectedMonth && (
                            <Link
                                underline="hover"
                                color="inherit"
                                sx={{ cursor: "pointer" }}
                                onClick={() => handleBreadcrumbClick("meetings")}
                            >
                                {selectedMonth.monthYear}
                            </Link>
                        )}
                        {selectedMeeting && (
                            <Typography color="text.primary">{selectedMeeting.title}</Typography>
                        )}
                    </Breadcrumbs>
                </Box>

                {/* Search Bar */}
                <Paper
                    elevation={0}
                    sx={{
                        p: "4px 12px",
                        display: "flex",
                        alignItems: "center",
                        width: { xs: "100%", sm: 300 },
                        borderRadius: "10px",
                        border: "1px solid #e2e8f0",
                        bgcolor: "white",
                    }}
                >
                    <SearchIcon sx={{ color: "text.secondary", mr: 1 }} />
                    <InputBase
                        placeholder="Search records..."
                        fullWidth
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </Paper>
            </Box>

            {/* Back Button */}
            {(view === "meetings" || view === "files") && (
                <Box sx={{ mb: 3 }}>
                    <IconButton
                        onClick={handleBack}
                        sx={{
                            bgcolor: "white",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                            "&:hover": { bgcolor: "#f1f5f9" },
                        }}
                    >
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography variant="button" sx={{ ml: 1, fontWeight: 600, color: "#64748b" }}>
                        Back
                    </Typography>
                </Box>
            )}

            {/* Main Content View */}
            <Box sx={{ mt: 2 }}>
                {view === "months" && renderMonths()}
                {view === "meetings" && renderMeetings()}
                {view === "files" && renderFiles()}
            </Box>

            {/* Empty State */}
            {filteredData.length === 0 && (
                <Box sx={{ textAlign: "center", py: 10 }}>
                    <FolderIcon sx={{ fontSize: 64, color: "#e2e8f0", mb: 2 }} />
                    <Typography variant="h6" color="textSecondary">
                        No records found matching your search.
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

export default MeetingRecordsPage;
